import { Colors } from "swing-components";

import { MarkdownContent } from "~components";
import {
  AccordionAnswer,
  CheckboxAnswer,
  ChoiceAnswer,
  ContentAnswer,
  DownloadAnswer,
  LinkAnswer,
  NumberAnswer,
  OutlineAnswer,
  RadioAnswer,
  SignatureAnswer,
  TextAnswer,
  UploadAnswer,
  WebinarConfirmationAnswer,
  WebinarDatesAnswer,
} from "~onboarding/components";
import type { AnswerType, QuestionComputed, UnsyncedSubAttribute } from "~onboarding/utils";
import { WebinarOption } from "~utils";

type QuestionProps = QuestionComputed & {
  /**
   * When answering a Question, this function will pass the `value` to the
   * parent Step component.
   */
  onChange: ({ attribute, value }: UnsyncedSubAttribute) => void;
  /**
   * Disables the editability of the Question
   */
  isReadOnly?: boolean;
};

export function Question(props: QuestionProps) {
  const {
    attribute,
    label,
    description,
    value,
    answerPlacement = "BOTTOM",
    onChange,
    isReadOnly,
    answerType,
  } = props;

  /***** Handler *****/
  /**
   * Pass the value to the parent Step component only when the Question is not
   * in a readonly state.
   */
  function handleChange(value: UnsyncedSubAttribute["value"]) {
    // Do not sent updates when the Question is read-only
    if (isReadOnly) return;

    // Otherwise, pass the identifier and value to the parent Step component
    onChange({ attribute, value });
  }

  return (
    <div
      css={{
        border: "none",
        backgroundColor: Colors.white200,
        display: "flex",
        flexDirection: "column",
        gap: 16,
      }}
    >
      {/* Answer */}
      {answerPlacement === "TOP" && (
        <Answer
          answerType={answerType}
          onChange={handleChange}
          value={value}
          isReadOnly={isReadOnly}
        />
      )}

      {/* Label */}
      <div css={{ margin: 0, fontWeight: 600, fontSize: 16, color: Colors.black }}>
        <MarkdownContent body={label} testId="question-label" />
      </div>

      {/* Description */}
      {description && (
        <p css={{ margin: 0, fontWeight: 400, fontSize: 16, color: Colors.black }}>{description}</p>
      )}

      {/* Answer */}
      {answerPlacement === "BOTTOM" && (
        <Answer
          answerType={answerType}
          onChange={handleChange}
          value={value}
          isReadOnly={isReadOnly}
        />
      )}
    </div>
  );
}

/********** Helper Component *********/
type AnswerProps = {
  answerType: AnswerType;
  onChange: (value: UnsyncedSubAttribute["value"]) => void;
  value?: string | object;
  isReadOnly?: boolean;
};

/** Renders any answer type */
function Answer(props: AnswerProps) {
  const { answerType, onChange, value, isReadOnly } = props;
  const { type } = answerType;

  switch (type) {
    /***** Render - Content Types */
    case "ACCORDION":
      return <AccordionAnswer {...answerType} onChange={onChange} isReadOnly={isReadOnly} />;
    case "CONTENT":
      return <ContentAnswer {...answerType} onChange={onChange} isReadOnly={isReadOnly} />;
    case "OUTLINE":
      return <OutlineAnswer {...answerType} onChange={onChange} isReadOnly={isReadOnly} />;

    /***** Render - Click Types */
    case "LINK":
      return <LinkAnswer {...answerType} onChange={onChange} isReadOnly={isReadOnly} />;
    case "DOWNLOAD":
      return <DownloadAnswer {...answerType} onChange={onChange} isReadOnly={isReadOnly} />;
    case "UPLOAD":
      return (
        <UploadAnswer {...answerType} onChange={onChange} isReadOnly={isReadOnly || !!value} />
      );

    /***** Render - Input Types */
    case "TEXT":
      return (
        <TextAnswer
          {...answerType}
          onChange={onChange}
          value={value as string}
          isReadOnly={isReadOnly}
        />
      );
    case "NUMBER":
      return (
        <NumberAnswer
          {...answerType}
          onChange={onChange}
          value={value as string}
          isReadOnly={isReadOnly}
        />
      );

    /***** Render - List Types */
    case "RADIO":
      return (
        <RadioAnswer
          {...answerType}
          onChange={onChange}
          value={value as string}
          isReadOnly={isReadOnly}
        />
      );
    case "CHECKBOX":
      return (
        <CheckboxAnswer
          {...answerType}
          onChange={onChange}
          value={value as string}
          isReadOnly={isReadOnly}
        />
      );
    case "WEBINAR_DATES":
      return (
        <WebinarDatesAnswer
          {...answerType}
          onChange={onChange}
          value={value as WebinarOption}
          isReadOnly={isReadOnly}
        />
      );
    case "WEBINAR_CONFIRMATION":
      return <WebinarConfirmationAnswer {...answerType} value={value as WebinarOption} />;
    case "SIGNATURE":
      return (
        <SignatureAnswer {...answerType} onChange={onChange} isReadOnly={isReadOnly || !!value} />
      );
    case "CHOICE":
      return (
        <ChoiceAnswer
          {...answerType}
          onChange={onChange}
          value={value as string}
          isReadOnly={isReadOnly}
        />
      );
    default: {
      // Exhaustive check: if we add a new type to AnswerType, TypeScript will error here if not handled above
      void (type as never);
      return null;
    }
  }
}
